"use client";
import React, { useState } from "react";
import { Eye, EyeOff, AlertCircle } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Link } from "react-router-dom";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    // Client-side validation
    const newFieldErrors: { email?: string; password?: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      newFieldErrors.email = "Please enter your email address";
    } else if (!emailRegex.test(email)) {
      newFieldErrors.email = "Invalid Email format";
    }

    if (!password) {
      newFieldErrors.password = "Please enter your password";
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    setLoading(true);

    try {
      await login(email, password, remember);
      // Navigation handled by AuthContext
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex font-sans bg-white overflow-hidden">
      {/* Left Side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-12 lg:px-20 overflow-y-auto">
        <div className="max-w-[340px] w-full mx-auto py-8">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/glossa-purple.png"
              alt="Glossa"
              className="h-8 sm:h-9 max-w-[120px] pointer-events-none select-none"
            />
          </div>

          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
              Glossa for professionals
            </h1>
            <p className="text-gray-500 text-[13px]">
              Create an account or log in to manage your business
            </p>
          </div>

          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <div className="space-y-3">
              {/* Email Field */}
              <div className="space-y-1">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldErrors.email)
                      setFieldErrors({ ...fieldErrors, email: "" });
                  }}
                  className={`w-full h-[42px] px-4 bg-[#F9FAFB] border ${
                    fieldErrors.email ? "border-red-500" : "border-gray-100"
                  } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400 font-normal`}
                />
                {fieldErrors.email && (
                  <p className="text-[11px] text-red-500 font-medium ml-1">
                    {fieldErrors.email}
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div className="space-y-1">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password)
                        setFieldErrors({ ...fieldErrors, password: "" });
                    }}
                    className={`w-full h-[42px] px-4 bg-[#F9FAFB] border ${
                      fieldErrors.password
                        ? "border-red-500"
                        : "border-gray-100"
                    } rounded-lg text-sm text-gray-900 focus:bg-white focus:ring-1 focus:ring-[#885ABB] focus:border-[#885ABB] transition-all outline-none placeholder:text-gray-400 font-normal`}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#885ABB]"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="text-[11px] text-red-500 font-medium ml-1">
                    {fieldErrors.password}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between text-[11px] font-medium py-1">
              <label className="flex items-center text-gray-500 cursor-pointer hover:text-gray-700 transition-colors">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-3.5 w-3.5 text-[#885ABB] border-gray-300 rounded focus:ring-[#885ABB] transition-all"
                />
                <span className="ml-2">Remember me</span>
              </label>

              <Link
                to="/forgot-password"
                className="text-[#885ABB] hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <div className="p-3 text-red-600 bg-red-50 text-[11px] rounded-lg border border-red-100 flex items-center gap-2">
                <AlertCircle size={14} />
                <p className="font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#885ABB] hover:bg-[#7647a8] text-white h-[42px] rounded-lg font-bold text-sm transition-all disabled:opacity-50 shadow-lg shadow-purple-500/30"
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>

          <p className="text-center text-[11px] text-gray-500 pt-6 font-medium">
            Contact support if you need a new business account.
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-gray-50 max-h-screen overflow-hidden">
        <img
          src="/auth-cover.png"
          alt="Glossa"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
        />
      </div>
    </div>
  );
}
